import { create } from 'zustand';
import i18n from '../i18n';
import { loadTheme, getCurrentTheme, type ThemeName } from '../themes';

export interface SellerInfo {
  id: number;
  nickname: string;
  active: boolean;
  active_sites?: string[];
}

interface UIState {
  sidebarCollapsed: boolean;
  sidebarMobileOpen: boolean;
  theme: ThemeName;
  language: string;
  notificationPanelOpen: boolean;
  sellerId: number;
  sellers: SellerInfo[];
  toggleSidebar: () => void;
  setSidebarMobileOpen: (open: boolean) => void;
  setTheme: (theme: ThemeName) => void;
  setLanguage: (lang: string) => void;
  toggleNotificationPanel: () => void;
  setSellerId: (id: number) => void;
  setSellers: (sellers: SellerInfo[]) => void;
  /** R21 — Acota el vendedor elegido a los del usuario. Ver abajo. */
  ensureSellerScope: (sellerIds: number[] | null | undefined) => void;
  aiChatOpen: boolean;
  toggleAiChat: () => void;
}

export const useUIStore = create<UIState>((set) => ({
  sidebarCollapsed: false,
  sidebarMobileOpen: false,
  theme: getCurrentTheme(),
  language: localStorage.getItem('cbt-v2-lang') || 'es',
  notificationPanelOpen: false,
  sellerId: parseInt(localStorage.getItem('cbt-v2-seller') || '1', 10),
  sellers: [],

  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

  setSidebarMobileOpen: (open: boolean) => set({ sidebarMobileOpen: open }),

  setTheme: (theme: ThemeName) => {
    loadTheme(theme);
    set({ theme });
  },

  setLanguage: (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('cbt-v2-lang', lang);
    set({ language: lang });
  },

  toggleNotificationPanel: () =>
    set((s) => ({ notificationPanelOpen: !s.notificationPanelOpen })),

  setSellerId: (id: number) => {
    localStorage.setItem('cbt-v2-seller', String(id));
    set({ sellerId: id });
  },

  setSellers: (sellers: SellerInfo[]) => set({ sellers }),

  /**
   * R21 — El vendedor elegido arranca en lo último guardado en el navegador, o en el 1 si no hay
   * nada; y cada pantalla lo manda en `?seller_id=`. A quien no puede ver todos los vendedores no
   * se le muestra el selector, así que un usuario del vendedor 6 pedía todo con `seller_id=1`.
   *
   * Esto lo corrige al entrar (login y al validar la sesión): si el vendedor elegido no es uno de
   * los suyos, se pasa al primero de su lista y se guarda. A quien SÍ puede ver todos los
   * vendedores no se lo llama nunca: elige libremente.
   */
  ensureSellerScope: (sellerIds) => {
    if (!sellerIds || sellerIds.length === 0) return;
    set((s) => {
      if (sellerIds.includes(s.sellerId)) return s;
      localStorage.setItem('cbt-v2-seller', String(sellerIds[0]));
      return { sellerId: sellerIds[0] };
    });
  },

  aiChatOpen: false,
  toggleAiChat: () => set((s) => ({ aiChatOpen: !s.aiChatOpen })),
}));
